class ImagePeekSlider extends HTMLElement {
  constructor() {
    super();

    this.viewport = null;
    this.track = null;
    this.originalSlides = [];
    this.startX = 0;
    this.startScrollLeft = 0;
    this.dragDistance = 0;
    this.isDragging = false;
    this.isPointerDown = false;
    this.isTouchScrolling = false;
    this.activePointerType = null;
    this.isHovering = false;
    this.isWheelScrolling = false;
    this.isBlockSelected = false;
    this.isVisible = true;
    this.loopStart = 0;
    this.loopWidth = 0;
    this.movementRemainder = 0;
    this.mobileAnimationPosition = null;
    this.mobileSubpixelOffset = 0;
    this.mobileMediaReady = window.innerWidth >= 768;
    this.parallaxPending = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartScrollLeft = 0;
    this.touchDirection = null;
    this.lastScrollY = window.scrollY;
    this.lastAnimationTime = 0;
    this.animationFrame = null;
    this.normalizeFrame = null;
    this.wheelEndTimer = null;
    this.touchResumeTimer = null;

    this.onAnimationFrame = this.onAnimationFrame.bind(this);
    this.onViewportScroll = this.onViewportScroll.bind(this);
    this.onWindowScroll = this.onWindowScroll.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onGlobalPointerEnd = this.onGlobalPointerEnd.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onGlobalTouchEnd = this.onGlobalTouchEnd.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-image-peek-viewport]');
    this.track = this.querySelector('.image-peek-slider__track');
    if (!this.viewport || !this.track) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reducedMotion;
    this.autoplaySpeed = Number.parseFloat(this.dataset.autoplaySpeed) || 30;
    this.parallaxEnabled = this.dataset.parallax === 'true' && !this.reducedMotion;
    this.parallaxStrength = Number.parseFloat(this.dataset.parallaxStrength) || 0.5;

    this.setupLoop();
    this.prepareMobileMedia();

    this.viewport.addEventListener('scroll', this.onViewportScroll, { passive: true });
    this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('pointermove', this.onPointerMove);
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerUp);
    this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('pointerup', this.onGlobalPointerEnd);
    window.addEventListener('pointercancel', this.onGlobalPointerEnd);
    window.addEventListener('touchend', this.onGlobalTouchEnd, { passive: true });
    window.addEventListener('touchcancel', this.onGlobalTouchEnd, { passive: true });
    this.viewport.addEventListener('mouseenter', this.onMouseEnter);
    this.viewport.addEventListener('mouseleave', this.onMouseLeave);
    this.viewport.addEventListener('click', this.onClick, true);
    this.viewport.addEventListener('dragstart', this.onDragStart);
    this.viewport.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    this.resizeObserver = new ResizeObserver(() => this.refreshLoopMetrics(false));
    this.resizeObserver.observe(this.viewport);

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
        rootMargin: '150px 0px',
        threshold: 0,
      });
      this.intersectionObserver.observe(this);
    } else {
      this.startAnimation();
      if (this.parallaxEnabled) window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    if (!this.viewport) return;

    this.stopAnimation();
    this.viewport.removeEventListener('scroll', this.onViewportScroll);
    this.viewport.removeEventListener('wheel', this.onWheel);
    this.viewport.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport.removeEventListener('pointermove', this.onPointerMove);
    this.viewport.removeEventListener('pointerup', this.onPointerUp);
    this.viewport.removeEventListener('pointercancel', this.onPointerUp);
    this.viewport.removeEventListener('touchstart', this.onTouchStart);
    this.viewport.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('pointerup', this.onGlobalPointerEnd);
    window.removeEventListener('pointercancel', this.onGlobalPointerEnd);
    window.removeEventListener('touchend', this.onGlobalTouchEnd);
    window.removeEventListener('touchcancel', this.onGlobalTouchEnd);
    this.viewport.removeEventListener('mouseenter', this.onMouseEnter);
    this.viewport.removeEventListener('mouseleave', this.onMouseLeave);
    this.viewport.removeEventListener('click', this.onClick, true);
    this.viewport.removeEventListener('dragstart', this.onDragStart);
    this.viewport.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('scroll', this.onWindowScroll);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.normalizeFrame);
    window.clearTimeout(this.wheelEndTimer);
    window.clearTimeout(this.touchResumeTimer);
  }

  setupLoop() {
    this.track.querySelectorAll('[data-image-peek-clone]').forEach((clone) => clone.remove());
    this.originalSlides = Array.from(this.track.querySelectorAll('[data-image-peek-slide]'));
    if (!this.originalSlides.length) return;

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    this.originalSlides.forEach((slide) => before.append(this.createClone(slide)));
    this.originalSlides.forEach((slide) => after.append(this.createClone(slide)));
    this.track.insertBefore(before, this.originalSlides[0]);
    this.track.append(after);

    requestAnimationFrame(() => this.refreshLoopMetrics(true));
  }

  createClone(slide) {
    const clone = slide.cloneNode(true);

    clone.dataset.imagePeekClone = '';
    clone.removeAttribute('data-block-id');
    clone.removeAttribute('data-shopify-editor-block');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('a, button, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
    clone.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.decoding = 'async';
      image.fetchPriority = 'low';
    });

    return clone;
  }

  prepareMobileMedia() {
    if (window.innerWidth >= 768) {
      this.mobileMediaReady = true;
      return;
    }

    this.mobileMediaReady = false;
    const leadImages = this.originalSlides
      .slice(0, 3)
      .flatMap((slide) => Array.from(slide.querySelectorAll('img')));

    leadImages.forEach((image) => {
      image.loading = 'eager';
      image.decoding = 'async';
    });

    const decodeImages = leadImages.map((image) => {
      if (typeof image.decode !== 'function') return Promise.resolve();
      return image.decode().catch(() => {});
    });

    Promise.all(decodeImages)
      .then(() => new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      }))
      .then(() => {
        if (!this.isConnected) return;
        this.mobileMediaReady = true;
        this.clearMobileSubpixelOffset();
        this.startAnimation();
      });
  }

  refreshLoopMetrics(resetPosition) {
    if (!this.originalSlides.length) return;

    this.clearMobileSubpixelOffset();

    const allSlides = Array.from(this.track.querySelectorAll('[data-image-peek-slide]'));
    const originalFirst = this.originalSlides[0];
    const originalIndex = allSlides.indexOf(originalFirst);
    const afterFirst = allSlides[originalIndex + this.originalSlides.length];
    if (!afterFirst) return;

    const paddingStart = Number.parseFloat(getComputedStyle(this.viewport).scrollPaddingInlineStart) || 0;
    const nextLoopStart = originalFirst.offsetLeft - paddingStart;
    const nextLoopWidth = afterFirst.offsetLeft - originalFirst.offsetLeft;
    if (nextLoopWidth <= 0) return;

    const progress = this.loopWidth > 0
      ? (this.viewport.scrollLeft - this.loopStart) / this.loopWidth
      : 0;

    this.loopStart = nextLoopStart;
    this.loopWidth = nextLoopWidth;
    this.viewport.scrollLeft = resetPosition
      ? this.loopStart
      : this.loopStart + (((progress % 1) + 1) % 1) * this.loopWidth;
    this.normalizeLoop();
    this.startAnimation();
  }

  normalizeLoop() {
    if (!this.loopWidth) return;

    let scrollLeft = this.viewport.scrollLeft;
    while (scrollLeft < this.loopStart) scrollLeft += this.loopWidth;
    while (scrollLeft >= this.loopStart + this.loopWidth) scrollLeft -= this.loopWidth;

    if (Math.abs(scrollLeft - this.viewport.scrollLeft) > 0.5) {
      this.viewport.scrollLeft = scrollLeft;
    }
  }

  clearMobileSubpixelOffset() {
    if (!this.track) return;

    if (window.innerWidth < 768) {
      this.track.style.transform = 'translate3d(0, 0, 0)';
    } else {
      this.track.style.removeProperty('transform');
    }
    this.mobileAnimationPosition = null;
    this.mobileSubpixelOffset = 0;
  }

  moveMobileViewport(movement) {
    let position = this.mobileAnimationPosition ?? this.viewport.scrollLeft;
    position += movement;

    while (position < this.loopStart) position += this.loopWidth;
    while (position >= this.loopStart + this.loopWidth) position -= this.loopWidth;

    const scrollPosition = Math.floor(position);
    this.mobileSubpixelOffset = position - scrollPosition;
    this.mobileAnimationPosition = position;
    this.viewport.scrollLeft = scrollPosition;
    this.track.style.transform = `translate3d(${-this.mobileSubpixelOffset}px, 0, 0)`;
  }

  onIntersection(entries) {
    this.isVisible = entries[0]?.isIntersecting ?? true;

    if (this.isVisible) {
      this.lastScrollY = window.scrollY;
      if (this.parallaxEnabled) window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      this.startAnimation();
    } else {
      window.removeEventListener('scroll', this.onWindowScroll);
      this.stopAnimation();
    }
  }

  startAnimation() {
    if (this.animationFrame || !this.isVisible || (!this.autoplayEnabled && Math.abs(this.parallaxPending) < 0.1)) return;
    this.lastAnimationTime = 0;
    this.animationFrame = requestAnimationFrame(this.onAnimationFrame);
  }

  stopAnimation() {
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    this.lastAnimationTime = 0;
    this.classList.remove('is-autoplaying', 'is-parallax-scrolling');
  }

  onAnimationFrame(timestamp) {
    if (!this.animationFrame) return;

    const elapsed = this.lastAnimationTime
      ? Math.min(timestamp - this.lastAnimationTime, 50)
      : 0;
    this.lastAnimationTime = timestamp;
    const canAutoplay = this.autoplayEnabled
      && this.mobileMediaReady
      && !this.isPointerDown
      && !this.isHovering
      && !this.isWheelScrolling
      && !this.isBlockSelected
      && !document.hidden;
    const keepAutoplayMode = this.autoplayEnabled
      && this.mobileMediaReady
      && !this.isPointerDown
      && !this.isWheelScrolling
      && !this.isBlockSelected
      && !document.hidden;
    let movement = canAutoplay ? this.autoplaySpeed * elapsed / 1000 : 0;

    if (this.mobileMediaReady && !this.isPointerDown && Math.abs(this.parallaxPending) >= 0.1) {
      const frameRatio = elapsed / (1000 / 60);
      const damping = 1 - Math.pow(0.86, frameRatio);
      const parallaxStep = Math.sign(this.parallaxPending)
        * Math.min(Math.abs(this.parallaxPending) * damping, 14 * frameRatio);
      this.parallaxPending -= parallaxStep;
      movement += parallaxStep;
      this.classList.add('is-parallax-scrolling');
    } else {
      this.parallaxPending = 0;
      this.classList.remove('is-parallax-scrolling');
    }

    this.classList.toggle('is-autoplaying', keepAutoplayMode);
    if (movement) {
      if (window.innerWidth < 768 && this.loopWidth) {
        this.moveMobileViewport(movement);
      } else {
        this.movementRemainder += movement;
        const previousScrollLeft = this.viewport.scrollLeft;

        this.viewport.scrollLeft = previousScrollLeft + this.movementRemainder;
        const appliedMovement = this.viewport.scrollLeft - previousScrollLeft;

        if (appliedMovement) {
          this.movementRemainder -= appliedMovement;
          this.normalizeLoop();
        }
      }
    }

    if (canAutoplay || Math.abs(this.parallaxPending) >= 0.1) {
      this.animationFrame = requestAnimationFrame(this.onAnimationFrame);
    } else {
      this.animationFrame = null;
    }
  }

  onViewportScroll() {
    if (this.isTouchScrolling) this.queueTouchResume();
    if (this.animationFrame || this.isDragging || this.isWheelScrolling) return;
    if (this.normalizeFrame) return;

    this.normalizeFrame = requestAnimationFrame(() => {
      this.normalizeFrame = null;
      this.normalizeLoop();
    });
  }

  onWindowScroll() {
    const scrollY = window.scrollY;

    // Mobile Safari can omit a local pointerup when a vertical gesture becomes
    // page scrolling. Release that stale state so autoplay keeps running.
    if (this.activePointerType && this.activePointerType !== 'mouse') {
      this.releaseTouchInteraction();
    }

    this.parallaxPending += (scrollY - this.lastScrollY) * this.parallaxStrength;
    this.parallaxPending = Math.max(Math.min(this.parallaxPending, 280), -280);
    this.lastScrollY = scrollY;
    this.startAnimation();
  }

  onWheel(event) {
    if (this.dataset.desktopWheel !== 'true' || window.innerWidth < 768) return;
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    const deltaMultiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE
      ? 16
      : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
        ? this.viewport.clientWidth
        : 1;
    const delta = event.deltaX * deltaMultiplier;
    if (!delta) return;

    event.preventDefault();
    this.isWheelScrolling = true;
    this.classList.add('is-wheel-scrolling');
    this.viewport.scrollLeft += delta;
    this.normalizeLoop();

    window.clearTimeout(this.wheelEndTimer);
    this.wheelEndTimer = window.setTimeout(() => {
      this.isWheelScrolling = false;
      this.classList.remove('is-wheel-scrolling');
      this.startAnimation();
    }, 140);
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse') return;

    this.clearMobileSubpixelOffset();
    this.activePointerType = event.pointerType;
    this.parallaxPending = 0;
    this.movementRemainder = 0;

    if (event.button !== 0) return;

    this.isPointerDown = true;
    this.classList.remove('is-autoplaying', 'is-parallax-scrolling');
    this.isDragging = true;
    this.dragDistance = 0;
    this.startX = event.clientX;
    this.startScrollLeft = this.viewport.scrollLeft;
    this.viewport.setPointerCapture(event.pointerId);
    this.classList.add('is-dragging');
  }

  onPointerMove(event) {
    if (!this.isDragging) return;

    this.dragDistance = event.clientX - this.startX;
    this.viewport.scrollLeft = this.startScrollLeft - this.dragDistance;
    this.normalizeLoop();
  }

  onPointerUp(event) {
    if (event.pointerType !== 'mouse') return;

    this.isPointerDown = false;
    this.activePointerType = null;
    if (!this.isDragging) {
      this.startAnimation();
      return;
    }

    this.isDragging = false;
    if (this.viewport.hasPointerCapture(event.pointerId)) {
      this.viewport.releasePointerCapture(event.pointerId);
    }
    this.classList.remove('is-dragging');
    this.startAnimation();
  }

  onGlobalPointerEnd(event) {
    if (event.pointerType === 'mouse' || this.activePointerType === 'mouse') return;
    this.releaseTouchInteraction();
  }

  onTouchStart(event) {
    const touch = event.touches[0];
    if (!touch) return;

    this.clearMobileSubpixelOffset();
    window.clearTimeout(this.touchResumeTimer);
    this.isPointerDown = true;
    this.isTouchScrolling = true;
    this.activePointerType = 'touch';
    this.isHovering = false;
    this.parallaxPending = 0;
    this.movementRemainder = 0;
    this.dragDistance = 0;
    this.touchDirection = null;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartScrollLeft = this.viewport.scrollLeft;
    this.classList.remove('is-autoplaying', 'is-parallax-scrolling');
    this.classList.add('is-touch-scrolling');
  }

  onTouchMove(event) {
    const touch = event.touches[0];
    if (!touch || !this.isTouchScrolling) return;

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (!this.touchDirection && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 5) {
      this.touchDirection = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }
    if (this.touchDirection !== 'horizontal') return;

    event.preventDefault();
    this.dragDistance = deltaX;
    this.viewport.scrollLeft = this.touchStartScrollLeft - deltaX;
    this.normalizeLoop();
  }

  onGlobalTouchEnd() {
    if (this.activePointerType === 'mouse') return;
    this.releaseTouchInteraction();
  }

  releaseTouchInteraction() {
    if (!this.isPointerDown && !this.activePointerType && !this.isTouchScrolling) return;

    this.activePointerType = null;
    this.queueTouchResume();
  }

  queueTouchResume() {
    window.clearTimeout(this.touchResumeTimer);
    this.touchResumeTimer = window.setTimeout(() => {
      this.isPointerDown = false;
      this.isTouchScrolling = false;
      this.touchDirection = null;
      this.classList.remove('is-touch-scrolling');
      this.normalizeLoop();
      this.startAnimation();
    }, 180);
  }

  onMouseEnter() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.isHovering = true;
  }

  onMouseLeave() {
    if (!this.isHovering) return;
    this.isHovering = false;
    this.startAnimation();
  }

  onClick(event) {
    if (Math.abs(this.dragDistance) <= 5) return;

    event.preventDefault();
    event.stopPropagation();
    this.dragDistance = 0;
  }

  onDragStart(event) {
    if (this.isDragging) event.preventDefault();
  }

  onKeyDown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    this.clearMobileSubpixelOffset();
    const slide = this.originalSlides[0];
    if (!slide) return;

    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const direction = event.key === 'ArrowRight' ? 1 : -1;

    event.preventDefault();
    this.viewport.scrollBy({ left: direction * (slide.offsetWidth + gap), behavior: 'smooth' });
  }

  onBlockSelect(event) {
    const slide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
    if (!slide) return;

    this.clearMobileSubpixelOffset();
    this.isBlockSelected = true;
    slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  onBlockDeselect() {
    this.isBlockSelected = false;
    this.startAnimation();
  }

  onVisibilityChange() {
    this.lastAnimationTime = 0;
    if (!document.hidden) this.startAnimation();
  }
}

if (!customElements.get('image-peek-slider')) {
  customElements.define('image-peek-slider', ImagePeekSlider);
}

window.wetheme?.webcomponentRegistry?.register({ key: 'image-peek-slider' });
